import { useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useQuery } from "react-query"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"
import moment from "moment"
import { EditableRow, ExtractedDeclaration, PreviewState } from "./types"
import {
     API_ESPACE_FOURNISSEUR_EXTRACT_HT_TVA_WORKING_DAYS_CONSULTANT_NAME_FROM_INVOICE_URL_ENDPOINT,
     API_ESPACE_FOURNISSEUR_RESP_GET_DECLARATIONS_LISTING_ENDPOINT,
     API_URL_ESPACE_CONSULTANT,
} from "../../../../constants"
import Swal from "sweetalert2"

export const useMassSaisie = () => {
     const [searchParams] = useSearchParams()
     const navigate = useNavigate()
     const initialFormattedMois = searchParams.get("mois") || undefined
     const initialMois = initialFormattedMois ? moment(initialFormattedMois).format("YYYY-MM") : undefined

     // --- ÉTATS DE BASE ---
     const [mois, setMois] = useState<string | undefined>(initialMois)
     const [formattedMois, setFormattedMois] = useState<string | undefined>(initialFormattedMois)
     const [isExtracting, setIsExtracting] = useState(false)
     const [isSaving, setIsSaving] = useState(false)
     const [isDragging, setIsDragging] = useState(false)

     // --- ÉTATS DES DONNÉES ---
     const [rows, setRows] = useState<EditableRow[]>([])
     const [filesMap, setFilesMap] = useState<Record<string, File>>({})
     const [submissionErrors, setSubmissionErrors] = useState<string[]>([])
     const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])

     // --- GESTION DES FICHIERS ---
     const [factureFilesMap, setFactureFilesMap] = useState<Record<string, File>>({})
     const [expenseFilesMap, setExpenseFilesMap] = useState<Record<string, File[]>>({})

     // --- ÉTATS D'INTERFACE ---
     const [previewFile, setPreviewFile] = useState<PreviewState | null>(null)
     const [mobileView, setMobileView] = useState<"preview" | "form">("preview")
     const fileInputRef = useRef<HTMLInputElement>(null)

     // Récupération des consultants éligibles
     const listingQuery = useQuery<any, AxiosError>(
          ["QUERY_LISTING", formattedMois],
          () => axios.post(API_ESPACE_FOURNISSEUR_RESP_GET_DECLARATIONS_LISTING_ENDPOINT, { mois: formattedMois }).then(r => r.data),
          { enabled: !!formattedMois }
     )

     const availableConsultantIds = useMemo(() => {
          return listingQuery.data?.data.map((item: any) => item.consultant.id).filter((id: any) => !!id) || []
     }, [listingQuery.data])

     /**
      * Importation : Découpage PDF et Analyse IA
      */
     const handleFileDrop = async (files: FileList | null) => {
          if (!files || files.length === 0 || !availableConsultantIds.length || !formattedMois) return
          setIsExtracting(true)
          const finalFilesToExtract: File[] = Array.from(files)
          const newFilesMap = { ...filesMap }

          try {
               for (const f of finalFilesToExtract) {
                    newFilesMap[f.name] = f
               }
               setFilesMap(newFilesMap)

               const formData = new FormData()
               finalFilesToExtract.forEach((f, i) => formData.append(`files[${i}]`, f))
               formData.append("mois", formattedMois)
               availableConsultantIds.forEach((id: any, i: number) => formData.append(`consultantIds[${i}]`, id!.toString()))

               const response = await axios.post<{ declarations: any[] }>(API_ESPACE_FOURNISSEUR_EXTRACT_HT_TVA_WORKING_DAYS_CONSULTANT_NAME_FROM_INVOICE_URL_ENDPOINT, formData)

               const newRows: EditableRow[] = []
               const newFactureFilesMap = { ...factureFilesMap }

               response.data.declarations.forEach((fileGroup, fileIdx) => {
                    const currentFileName = fileGroup.fileName || finalFilesToExtract[fileIdx]?.name

                    Object.keys(fileGroup).forEach(key => {
                         if (key !== "fileName") {
                              const decl = fileGroup[key] as ExtractedDeclaration
                              const rowId = `row-${Date.now()}-${fileIdx}-${key}`

                              newRows.push({
                                   ...decl,
                                   fileName: currentFileName,
                                   id: rowId,
                                   selectedConsultantId: decl.consultantId || null,
                              })

                              const sourceFile = newFilesMap[currentFileName]
                              if (sourceFile) {
                                   newFactureFilesMap[rowId] = sourceFile
                              }
                         }
                    })
               })

               setFactureFilesMap(newFactureFilesMap)
               setRows(prev => [...prev, ...newRows])
               toast.success(`${newRows.length} ligne(s) ajoutée(s).`)
          } catch (e) {
               toast.error("Échec de l'analyse.")
          } finally {
               setIsExtracting(false)
          }
     }

     const updateRow = (id: string, field: keyof EditableRow, value: any) => {
          setRows(prev =>
               prev.map(r => {
                    if (r.id === id) {
                         const updated = { ...r, [field]: value }
                         updated.totalAmountHt = (Number(updated.prestationHt) || 0) + (Number(updated.fraisHt) || 0)
                         updated.totalVat = (Number(updated.prestationVat) || 0) + (Number(updated.fraisVat) || 0)
                         return updated
                    }
                    return r
               })
          )

          if (previewFile?.rowData.id === id) {
               setPreviewFile(prev => {
                    if (!prev) return null
                    const updatedRow = { ...prev.rowData, [field]: value }
                    updatedRow.totalAmountHt = (Number(updatedRow.prestationHt) || 0) + (Number(updatedRow.fraisHt) || 0)
                    updatedRow.totalVat = (Number(updatedRow.prestationVat) || 0) + (Number(updatedRow.fraisVat) || 0)
                    return { ...prev, rowData: updatedRow }
               })
          }
     }

     /**
      * Soumission : Envoi d'une facture et de plusieurs justificatifs par ligne
      */
     const handleSave = async () => {
          if (isSaving || selectedRowIds.length === 0) return

          setIsSaving(true)
          setSubmissionErrors([])
          const formData = new FormData()

          if (formattedMois) {
               const [year, month] = formattedMois.split("-")
               formData.append("mois", month)
               formData.append("annee", year)
          }

          rows.filter(r => selectedRowIds.includes(r.id)).forEach((row, index) => {
               formData.append(`declarations[${index}][consultantId]`, row.selectedConsultantId?.toString() || "")
               formData.append(`declarations[${index}][nbJoursTravailles]`, (row.nbJoursTravailles || 0).toString())
               formData.append(`declarations[${index}][prestationHt]`, row.prestationHt.toString())
               formData.append(`declarations[${index}][prestationVat]`, row.prestationVat.toString())
               formData.append(`declarations[${index}][fraisHt]`, row.fraisHt.toString())
               formData.append(`declarations[${index}][fraisVat]`, row.fraisVat.toString())

               const rowFacture = factureFilesMap[row.id]
               if (rowFacture) {
                    formData.append(`declarations[${index}][facture]`, rowFacture)
               }

               const rowJustifs = expenseFilesMap[row.id] || []
               rowJustifs.forEach(file => {
                    formData.append(`declarations[${index}][justificatifs][]`, file)
               })
          })

          try {
               const response = await axios.post<any>(`${API_URL_ESPACE_CONSULTANT}/declaration/add-facture-presta-st-on-mass`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
               })

               if (response.data.errors?.length > 0) {
                    setSubmissionErrors(response.data.errors)
               } else {
                    // Supprimer les lignes envoyées de l'état actuel
                    setRows(prev => prev.filter(r => !selectedRowIds.includes(r.id)))
                    setSelectedRowIds([])

                    Swal.fire({
                         title: "Envoi réussi !",
                         text: "Vos factures ont bien été transmises.",
                         icon: "success",
                         showCancelButton: true, // Réintroduit pour donner le choix
                         confirmButtonColor: "#3085d6",
                         cancelButtonColor: "#aaa",
                         confirmButtonText: "Continuer l'importation", // Rester sur la page
                         cancelButtonText: "Voir la liste", // Rediriger
                    }).then(result => {
                         // Si l'utilisateur clique sur "Voir la liste" (Annuler)
                         if (result.dismiss === Swal.DismissReason.cancel) {
                              navigate("/espace-resp-fournisseur/declarations/listing", { state: { mois: formattedMois } })
                         }
                         // S'il clique sur "Continuer", on ne fait rien (il reste sur la page avec les lignes restantes)
                    })
               }
          } catch (e) {
               toast.error("Erreur lors de l'envoi.")
          } finally {
               setIsSaving(false)
          }
     }

     return {
          state: {
               mois,
               formattedMois,
               isExtracting,
               isSaving,
               isDragging,
               rows,
               filesMap,
               submissionErrors,
               expenseFilesMap,
               factureFilesMap,
               selectedRowIds,
               previewFile,
               mobileView,
               listingQuery,
          },
          setters: {
               setMois,
               setFormattedMois,
               setIsDragging,
               setRows,
               setFilesMap,
               setSubmissionErrors,
               setExpenseFilesMap,
               setFactureFilesMap,
               setSelectedRowIds,
               setPreviewFile,
               setMobileView,
          },
          handlers: {
               handleFileDrop,
               updateRow,
               handleSave,
               handleAttachFactureFile: (rowId: string, files: FileList | null) => {
                    if (files && files[0]) setFactureFilesMap(prev => ({ ...prev, [rowId]: files[0] }))
               },
               handleRemoveFactureFile: (rowId: string) => {
                    setFactureFilesMap(prev => {
                         const copy = { ...prev }
                         delete copy[rowId]
                         return copy
                    })
               },
               handleAttachExpenseFiles: (rowId: string, files: FileList | null) => {
                    if (!files) return
                    setExpenseFilesMap(prev => ({ ...prev, [rowId]: [...(prev[rowId] || []), ...Array.from(files)] }))
               },
               handleRemoveExpenseFile: (rowId: string, idx: number) => {
                    setExpenseFilesMap(prev => ({ ...prev, [rowId]: prev[rowId].filter((_, i) => i !== idx) }))
               },
          },
          fileInputRef,
     }
}
